import { useEffect, useRef, useState } from 'react';

import { active } from 'src/components/languagePopup/LanguagePopupStyles';
import {
  Duration,
  Option,
  OptionState,
  SubscriptionResponseV2,
  SubscriptionState
} from 'src/types/subscription';
import { mixpanelObject } from 'src/utils/analytics';
import { putFacebookEvent } from 'src/utils/analytics/fbpixel/fbCapiHelper';
import { gtm } from 'src/utils/analytics/gtm';
import {
  MIXPANEL_COUNTER,
  MIXPANEL_UNIQUE_ID
} from 'src/utils/analytics/mixpanel/mixpanelConstants';
import {
  packToggleEvent,
  subscriptionDurationCTAEvent,
  subscriptionViewAllPacksCTA
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { ActivePromotions, DynamicObject } from 'src/utils/commonTypes';
import { GUEST_TOKEN } from 'src/utils/constants/localStorageConstants';
import { META_EVENTS } from 'src/utils/constants/metaPixelConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { fbqTrack } from 'src/utils/scripts/thirdPartyScripts';
import {
  calculateSellPriceNew,
  getCurrencySymbol,
  trackSelectedPlanMixpanelEvents,
  transformNewPlanToOldPlan
} from 'src/utils/SubscriptionHelper/SubscriptionHelper';
import { generateUUID } from 'src/utils/UtilityMethods';

import PromoTag from './BadgeTags/badgetags';
import FeatureCard from './Feature';
import PlanCard from './PlanCard';
import {
  whitePill,
  goldPill,
  Container,
  FeaturesContainer,
  Pill,
  PillsContainer,
  PlanCardContainer,
  transparentPill,
  IconStack,
  ViewOtherPlans,
  FeaturesContainerTitle,
  PromoBadgeWrapper
} from './styles';
import SubscribedPlanCard from './SubscribedPlanCard';
import { useAppStore } from '../../../hooks/useAppStore';
import {
  SUBSCRIPTION_ACTIONS,
  useSubscriptionDispatchV2,
  useSubscriptionStateV2
} from '../SubscriptionContext';

const getTitle = (duration: Duration, translations: DynamicObject) => {
  return `${duration.value} ${translations[duration?.label]}`;
};

const getPillVariant = (active: boolean) => {
  return active ? whitePill : transparentPill;
};

const PillsTray = ({
  data: durationList,
  activeOption,
  packToggleCounter
}: {
  data: SubscriptionResponseV2.DurationState[];
  activeOption?: SubscriptionResponseV2.DurationState;
  packToggleCounter: { current: number };
}) => {
  const {
    subscriptionData: currentSubscription,
    isUserLoggedIn,
    usersData,
    translations,
    remoteConfig
  } = useAppStore();
  const dispatch = useSubscriptionDispatchV2();
  const SubscriptionTranslations = translations?.subscription || {};
  const activePillRef = useRef<HTMLButtonElement>(null);
  const triggerPackToggleEvents = () => {
    packToggleCounter.current += 1;
    mixpanelObject.setProperty(MIXPANEL_COUNTER, packToggleCounter.current);
    mixpanelObject.setProperty(
      MIXPANEL_UNIQUE_ID,
      isUserLoggedIn ? usersData?.id : getLocalStorage(GUEST_TOKEN)
    );
    packToggleEvent();
  };

  const handlePillClick =
    (duration: SubscriptionResponseV2.DurationState) => () => {
      trackSelectedPlanMixpanelEvents(activeOption, !!currentSubscription);
      if (
        isFeatureEnabled(
          remoteConfig,
          FeatureFlagConstants.FEATURE_META_PIXEL_INTEGRATION
        )
      ) {
        const eventId = generateUUID();
        fbqTrack(META_EVENTS.Addtocart, eventId);
        putFacebookEvent(
          META_EVENTS.Addtocart,
          new Date().getTime(),
          window.location.href,
          eventId
        );
      }
      subscriptionDurationCTAEvent();
      triggerPackToggleEvents();
      gtm.packSelectedEventGTM(transformNewPlanToOldPlan(duration));
      dispatch({
        type: SUBSCRIPTION_ACTIONS.UPDATE_DURATION,
        payload: { id: duration?.id }
      });
    };

  return (
    <PillsContainer data-test-id="plan-duration-container">
      {durationList?.map((duration) => {
        const title =
          duration &&
          duration?.value &&
          String(duration?.value + ' ') +
            SubscriptionTranslations[duration?.label];
        return (
          <Pill
            ref={duration.active ? activePillRef : null}
            css={getPillVariant(duration?.active)}
            onClick={handlePillClick(duration)}
            key={duration?.id}>
            {title}
            {duration?.durationOfferLabel &&
              SubscriptionTranslations[duration?.durationOfferLabel] && (
                <PromoBadgeWrapper>
                  <PromoTag
                    height="14px"
                    text={
                      SubscriptionTranslations[duration?.durationOfferLabel]
                    }
                  />
                </PromoBadgeWrapper>
              )}
          </Pill>
        );
      })}
    </PillsContainer>
  );
};

const SubscribedPlan = ({
  data,
  isAtBestPlan
}: {
  data: Partial<SubscriptionResponseV2.Plan>;
  isAtBestPlan: boolean;
}) => {
  return <SubscribedPlanCard data={data} isAtBestPlan={isAtBestPlan} />;
};

const PlanCardTray = ({
  data: planList,
  packToggleCounter,
  activePromotions,
  isFullWidth = false
}: {
  data: SubscriptionResponseV2.Plan[];
  packToggleCounter: { current: number };
  activePromotions: ActivePromotions;
  isFullWidth?: boolean;
}) => {
  const [showAllPlans, setShowAllPlans] = useState(false);
  const activePlanRef = useRef<HTMLDivElement>(null);
  const { translations } = useAppStore();
  const NEW_SUBSCRIPTION_STRINGS = translations.subscription;
  const activePromoKeys = Object.keys(activePromotions || {});

  const handleViewOtherPlansClick = () => {
    setShowAllPlans(true);
    subscriptionViewAllPacksCTA();
  };

  const filteredPlans = showAllPlans
    ? planList
    : planList.filter((item) => item.type === 'reserved');

  const unreservedPlans = planList.filter((item) => item.type === 'unreserved');
  const isPromoInUnreserved = unreservedPlans.find(
    (item) => !!activePromoKeys.find((id) => id === item.id)
  );

  useEffect(() => {
    if (
      !showAllPlans &&
      Object.keys(activePromotions || {}).length &&
      isPromoInUnreserved
    ) {
      setShowAllPlans(true);
    }
  }, [activePromotions, showAllPlans]);

  useEffect(() => {
    if (activePlanRef.current) {
      activePlanRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [filteredPlans]);

  return (
    <PlanCardContainer>
      {filteredPlans.map((item, index) => (
        <PlanCard
          key={item.id}
          data={item}
          planRef={item.active ? activePlanRef : undefined}
          packToggleCounter={packToggleCounter}
          activePromotions={activePromotions}
          isFullWidth={isFullWidth && index <= 1}
        />
      ))}

      {!showAllPlans && unreservedPlans.length > 0 && (
        <ViewOtherPlans onClick={handleViewOtherPlansClick}>
          {NEW_SUBSCRIPTION_STRINGS.CMS_SELECT_ACCESS_LABEL_TEXT}
        </ViewOtherPlans>
      )}
    </PlanCardContainer>
  );
};

const FeaturesTray = ({
  data,
  currentPlan
}: {
  data: SubscriptionResponseV2.Plan;
  currentPlan: Partial<SubscriptionResponseV2.Plan> | undefined;
}) => {
  const { features: featuresList = [] } = data;
  const [item] = featuresList || [];
  const { translations } = useAppStore();
  const NEW_SUBSCRIPTION_STRINGS = translations.subscription;
  return (
    <FeaturesContainer>
      <FeaturesContainerTitle>
        {currentPlan && Object.keys(currentPlan).length > 0
          ? `${NEW_SUBSCRIPTION_STRINGS.CMS_UPGRADETO_TEXT} ${data.title}`
          : NEW_SUBSCRIPTION_STRINGS.CMS_PLAN_COVER_TITLE_BANNER_TEXT}
      </FeaturesContainerTitle>
      <IconStack>
        {item && <FeatureCard item={item} active={false} isFeature={true} />}
      </IconStack>
    </FeaturesContainer>
  );
};

const Plans = ({
  activePromotions,
  currentPlan,
  isAtBestPlan = false
}: {
  activePromotions: ActivePromotions;
  currentPlan: Partial<SubscriptionResponseV2.Plan> | undefined;
  isAtBestPlan: boolean;
}) => {
  const data = useSubscriptionStateV2();
  const packToggleCounter = useRef(0);
  if (!data) return <></>;
  const activeCategory = data.find((item) => item.active);
  const options = activeCategory?.options ?? [];
  const activeOption = options.find((item) => item.active);
  const isFullWidth =
    (activeCategory?.reserved?.length ?? 0) <= 2 ? true : false;
  return (
    <Container data-test-id="plans-container">
      {currentPlan && Object.keys(currentPlan).length > 0 && (
        <SubscribedPlan data={currentPlan} isAtBestPlan={isAtBestPlan} />
      )}
      {activeOption && !isAtBestPlan ? (
        <FeaturesTray data={activeOption} currentPlan={currentPlan} />
      ) : null}
      {!isAtBestPlan && (
        <PillsTray
          packToggleCounter={packToggleCounter}
          activeOption={activeCategory}
          data={data}
        />
      )}
      <PlanCardTray
        packToggleCounter={packToggleCounter}
        data={options}
        activePromotions={activePromotions}
        isFullWidth={isFullWidth}
      />
    </Container>
  );
};

export default Plans;
